import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { clearUserData } from '../slices/loginSlice';

const IDLE_TIMEOUT = 2 * 60 * 60 * 1000;  // 2 hours
const WARN_BEFORE  = 60 * 1000;            // warn 1 minute before logout
const CHECK_EVERY  = 10 * 1000;       // poll every 10 s

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function useIdleTimeout() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const lastActive = useRef(Date.now());
  const warned     = useRef(false);
  const loggedOut  = useRef(false);

  const resetActivity = useCallback(() => {
    lastActive.current = Date.now();
    if (warned.current) {
      warned.current = false;
      Swal.close();
    }
  }, []);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, resetActivity, { passive: true })
    );

    const interval = setInterval(() => {
      const idle = Date.now() - lastActive.current;

      // ── logout ──────────────────────────────────────────────────────────
      if (idle >= IDLE_TIMEOUT && !loggedOut.current) {
        loggedOut.current = true;
        Swal.close();
        dispatch(clearUserData());
        navigate('/login', { replace: true });
        Swal.fire({
          icon: 'warning',
          title: 'Session Expired',
          text: 'You were logged out after 2 hours of inactivity.',
          confirmButtonText: 'Sign In Again',
          confirmButtonColor: '#00c96e',
        });
        return;
      }

      // ── warning toast (1 min before) ────────────────────────────────────
      if (idle >= IDLE_TIMEOUT - WARN_BEFORE && !warned.current && !loggedOut.current) {
        warned.current = true;
        Swal.fire({
          icon: 'info',
          title: 'Still there?',
          html: 'You will be logged out in <b>1 minute</b> due to inactivity.<br/>Move your mouse or press a key to stay.',
          toast: false,
          position: 'center',
          timer: WARN_BEFORE,
          timerProgressBar: true,
          showConfirmButton: true,
          confirmButtonText: 'Keep me signed in',
          confirmButtonColor: '#00c96e',
        }).then((result) => {
          if (result.isConfirmed) {
            resetActivity();
          }
        });
      }
    }, CHECK_EVERY);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach(e =>
        window.removeEventListener(e, resetActivity)
      );
    };
  }, [dispatch, navigate, resetActivity]);
}
